import React, { useState } from 'react';
import StbButton from '../security-toolbox-ui/StbButton';
import { useFireLabTrigger, useLabRunTriggers } from '../../hooks/lab-hooks';
import { labTriggerActionLabel, labTriggerTypeLabel } from '../../support/labService';
import { LabDate, LabPanelState, LabStatusTag, labErrorMessage } from './LabShared';

export type LabRunTriggersPanelProps = {
  runId: number;
  /** Firing by hand is a proctor action. */
  canFire?: boolean;
};

const STATE_SENTIMENTS = {
  armed: 'info',
  holding: 'warning',
  fired: 'success',
  skipped: 'info',
  error: 'danger',
} as const;

/**
 * The triggers armed on this run, and the ability to fire one now.
 *
 * Firing by hand is how a proctor releases a gated step ahead of its condition - the fix
 * during an event when the timer is wrong or somebody needs to be unblocked. It is also the
 * only way a `manual` trigger ever fires.
 */
const LabRunTriggersPanel: React.FC<LabRunTriggersPanelProps> = ({ runId, canFire = false }) => {
  const [error, setError] = useState<string | null>(null);

  const { data: triggers, isLoading, error: fetchError } = useLabRunTriggers(runId);
  const fire = useFireLabTrigger();

  const handleFire = async (triggerKey: string) => {
    setError(null);

    try {
      await fire.mutateAsync({ runId, triggerKey });
    } catch (exc) {
      setError(labErrorMessage(exc, 'Could not fire that trigger.'));
    }
  };

  return (
    <LabPanelState
      loading={isLoading}
      error={fetchError}
      empty={!(triggers || []).length}
      emptyMessage="No triggers on this run."
      loadingLabel="Loading triggers..."
    >
      <>
        {error && <p className="m-0 mb-2 text-sm text-amber-800">{error}</p>}

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2 font-semibold">Trigger</th>
                <th className="px-3 py-2 font-semibold">Fires</th>
                <th className="px-3 py-2 font-semibold">Does</th>
                <th className="px-3 py-2 font-semibold">State</th>
                <th className="px-3 py-2 font-semibold">Fired</th>
                {canFire && <th className="px-3 py-2 font-semibold text-right">Actions</th>}
              </tr>
            </thead>

            <tbody>
              {(triggers || []).map((trigger) => (
                <tr key={trigger.id} className="border-b border-gray-100 align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium">{trigger.trigger_key}</div>
                    <div className="text-xs text-gray-500">{trigger.trigger_name}</div>
                    {trigger.error_message && (
                      <div className="mt-1 text-xs text-red-700">{trigger.error_message}</div>
                    )}
                  </td>

                  <td className="px-3 py-2 whitespace-nowrap">
                    {labTriggerTypeLabel(trigger.trigger_type)}
                  </td>

                  <td className="px-3 py-2 whitespace-nowrap">
                    {labTriggerActionLabel(trigger.action)}
                  </td>

                  <td className="px-3 py-2 whitespace-nowrap">
                    <LabStatusTag
                      label={trigger.state}
                      sentiment={STATE_SENTIMENTS[trigger.state] || 'info'}
                    />
                    {trigger.state === 'holding' && trigger.fire_after && (
                      <div className="text-xs text-gray-500">
                        {'fires at '}
                        <LabDate value={trigger.fire_after} />
                      </div>
                    )}
                  </td>

                  <td className="px-3 py-2 whitespace-nowrap">
                    <LabDate value={trigger.fired_at} />
                    {trigger.fire_count > 1 && (
                      <div className="text-xs text-gray-500">{`${trigger.fire_count} times`}</div>
                    )}
                  </td>

                  {canFire && (
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end">
                        <StbButton
                          role="request"
                          size="xsmall"
                          loading={fire.isPending}
                          onClick={async () => handleFire(trigger.trigger_key)}
                        >
                          Fire now
                        </StbButton>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    </LabPanelState>
  );
};

export default LabRunTriggersPanel;
